import { useMemo, useState } from 'react';
import { ChevronDown, Undo2 } from 'lucide-react';
import type { RoastProfileEdit, RoastProfileEditableKey } from './types';
import { EDITABLE_SERIES_KEYS, EDITABLE_SERIES_LABEL_KEY } from './RoastProfileUtils';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastProfileEditLedgerProps {
  edits: RoastProfileEdit[];
  onResetField: (key: RoastProfileEditableKey) => void;
  onResetAll: () => void;
}

/** How many individual changes each column spells out before it summarises the rest. */
const DETAIL_PREVIEW_LIMIT = 6;

const formatValue = (value: number | null) => (value === null ? '—' : String(value));

/**
 * Sits above the profile table and accounts for every value that no longer matches
 * the imported file: how many, in which columns, and what each one was. Reverting is
 * offered at the two scopes the table itself cannot: a whole column, or everything.
 * Single cells revert in place, from the cell.
 *
 * The table often renders inside a narrow grid column, so nothing here may depend on
 * viewport breakpoints: the summary is one non-wrapping row that truncates, and each
 * detail entry stacks its label and values instead of competing for one line.
 */
export function RoastProfileEditLedger({ edits, onResetField, onResetAll }: RoastProfileEditLedgerProps) {
  const { t } = useLanguage();
  const [showDetail, setShowDetail] = useState(false);

  const groups = useMemo(() => {
    const byKey = new Map<RoastProfileEditableKey, RoastProfileEdit[]>();
    for (const edit of edits) {
      const bucket = byKey.get(edit.key);
      if (bucket) bucket.push(edit);
      else byKey.set(edit.key, [edit]);
    }
    return EDITABLE_SERIES_KEYS.filter(key => byKey.has(key)).map(key => ({
      key,
      label: t(`roast.profileTable.${EDITABLE_SERIES_LABEL_KEY[key]}`),
      items: byKey.get(key)!.sort((a, b) => a.seconds - b.seconds),
    }));
  }, [edits, t]);

  if (edits.length === 0) return null;

  return (
    // The solid left rule and the filled button are where the accent is spent; the
    // surface stays pale so #0277BD keeps its contrast on the small type above it.
    <div
      className="border-t border-t-edited-100 border-l-4 border-l-edited-800 bg-edited-50/60 animate-slide-down motion-reduce:animate-none"
      data-test-id="roast-profile-edit-ledger"
    >
      <div className="flex items-center gap-2 px-2 py-2">
        <button
          type="button"
          onClick={() => setShowDetail(v => !v)}
          aria-expanded={showDetail}
          data-test-id="roast-profile-edit-ledger-toggle"
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-edited-800"
        >
          <span
            aria-hidden="true"
            className="h-0 w-0 shrink-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-edited-800"
          />
          <span className="truncate text-[11px] font-semibold text-edited-800">
            {edits.length === 1
              ? t('roast.profileTable.edits.summaryOne')
              : t('roast.profileTable.edits.summaryMany', { count: edits.length })}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-edited-400 transition-transform motion-reduce:transition-none ${
              showDetail ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>

        <button
          type="button"
          onClick={onResetAll}
          data-test-id="roast-profile-reset-all"
          className="shrink-0 whitespace-nowrap rounded-md bg-edited-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-edited-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-edited-800 focus-visible:ring-offset-1 motion-reduce:transition-none"
        >
          {t('roast.profileTable.edits.resetAll')}
        </button>
      </div>

      {showDetail && (
        <ul className="px-3 pb-2">
          {groups.map(group => {
            const shown = group.items.slice(0, DETAIL_PREVIEW_LIMIT);
            const hidden = group.items.length - shown.length;

            return (
              <li key={group.key} className="border-t border-edited-100 py-1.5 first:border-t-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[10px] font-bold uppercase tracking-wider text-edited-800">
                    {group.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => onResetField(group.key)}
                    aria-label={t('roast.profileTable.edits.resetField', { field: group.label })}
                    data-test-id={`roast-profile-reset-field-${group.key}`}
                    className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold text-edited-800 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-edited-800 motion-reduce:transition-none"
                  >
                    <Undo2 className="h-3 w-3" />
                    {t('roast.profileTable.edits.reset')}
                  </button>
                </div>

                {/* The accent is spent only on the value that changed; the imported number and
                    the timestamp stay neutral so the eye lands on the difference. */}
                <p className="mt-0.5 text-[11px] leading-relaxed text-gray-600">
                  {shown.map((edit, i) => (
                    <span key={`${edit.seconds}-${edit.key}`} className="whitespace-nowrap">
                      {i > 0 && <span className="text-gray-300"> · </span>}
                      <span className="tabular-nums">{edit.time}</span>{' '}
                      <span className="tabular-nums">{formatValue(edit.baseline)}</span>
                      <span className="px-0.5 text-gray-400">→</span>
                      <span className="font-semibold tabular-nums text-edited-800">{formatValue(edit.current)}</span>
                    </span>
                  ))}
                  {hidden > 0 && (
                    <span className="text-gray-500"> {t('roast.profileTable.edits.andMore', { count: hidden })}</span>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
